import {yupResolver} from '@hookform/resolvers/yup'
import {forgotPasswordStepsComponents} from 'constants/auth'
import useForgotPassword from 'logic/Auth/useForgotPassword'
import useVerifySms from 'logic/Auth/useVerifySms'
import {useEffect, useMemo, useState} from 'react'
import {useForm} from 'react-hook-form'
import {useNavigate} from 'react-router-dom'
import analyticsService from 'services/analytic'
import {forgotPasswordSchema} from 'services/utils/validationsSchemas'

const useForgotPasswordScreen = () => {
    const navigate = useNavigate()
    const [activeStep, setActiveStep] = useState(0)
    const [generalError, setGeneralError] = useState(null)

    const {
        doVerifySms,
        doSendSms,
        isLoading: isLoadingVerifySms,
        verifySmsError,
        sendSmsError,
        signupVerified,
        clearVerifySmsError,
        smsSent,
    } = useVerifySms(true)

    const {
        doForgotPassword,
        forgotPasswordResponse,
        forgotPasswordError,
        clearForgotPasswordError,
    } = useForgotPassword()

    const methods = useForm({
        mode: 'all',
        resolver: yupResolver(forgotPasswordSchema),
    })

    const {watch, handleSubmit} = methods
    const values = watch()

    const onSubmit = async (submittedValues) => {
        setGeneralError(null)
        clearForgotPasswordError()
        clearVerifySmsError()
        doForgotPassword(submittedValues)
    }

    useEffect(() => {
        if (forgotPasswordError) {
            setGeneralError(forgotPasswordError)
        }
    }, [forgotPasswordError])

    const handleSendSms = async () => {
        doSendSms({
            phone: values.username,
        })
    }

    useEffect(() => {
        if (smsSent) {
            goNext()
        }
    }, [smsSent])

    useEffect(() => {
        if (sendSmsError) {
            setGeneralError(sendSmsError)
        }
    }, [sendSmsError])

    const handleVerifySms = () => {
        doVerifySms({
            phone: values.username,
            sms: values.sms,
        })
    }

    useEffect(() => {
        if (signupVerified) {
            goNext()
        }
    }, [signupVerified])

    useEffect(() => {
        if (verifySmsError) {
            setGeneralError(verifySmsError)
        }
    }, [verifySmsError])

    const goNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1)
    }

    const handleNext = async () => {
        setGeneralError(null)
        switch (activeStep) {
            case 0:
                handleSendSms()
                analyticsService.forgotPasswordPressed('next_on_phone_number')
                break
            case 1:
                handleVerifySms()
                analyticsService.forgotPasswordPressed(
                    'next_on_verify_phone_number'
                )
                break
            case 2:
                analyticsService.forgotPasswordPressed('update_password')
                goNext()
                break
            case 3:
                break
            default:
                goNext()
        }
    }

    const handleBack = () => {
        setGeneralError(null)
        setActiveStep((prevActiveStep) => prevActiveStep - 1)
        if (activeStep === 1) {
            analyticsService.forgotPasswordPressed(
                'back_on_verify_phone_number'
            )
        }
    }

    const onDone = () => {
        navigate('/login')
        analyticsService.forgotPasswordPressed('launch_on_final_screen')
    }

    const forgotPasswordSteps = Object.keys(forgotPasswordStepsComponents)

    const buttonText = useMemo(() => {
        return activeStep === forgotPasswordSteps.length - 1
            ? 'updatePassword'
            : 'next'
    }, [activeStep, forgotPasswordSteps.length])

    const forgotPassword = handleSubmit(onSubmit)

    return {
        generalError,
        forgotPasswordSteps,
        onDone,
        handleBack,
        handleNext,
        isLoadingVerifySms,
        forgotPasswordResponse,
        forgotPassword,
        activeStep,
        methods,
        buttonText,
    }
}

export default useForgotPasswordScreen
